import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LightBulbIcon,
  MagnifyingGlassIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { editIntelligenceApi, hmdaApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import clsx from 'clsx';

const SEVERITY_BADGE: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-700',
  MEDIUM: 'bg-yellow-100 text-yellow-700',
  LOW: 'bg-green-100 text-green-700',
};

export default function EditIntelligencePage() {
  const [selectedValidationId, setSelectedValidationId] = useState<string>('');

  // Fetch validations for the selector
  const { data: validationsData } = useQuery({
    queryKey: ['hmda-validations'],
    queryFn: () => hmdaApi.getValidations({ limit: 50 }),
  });
  const validations = validationsData?.data?.validations || [];

  const queryParams = selectedValidationId
    ? { validationId: selectedValidationId }
    : undefined;

  // Systemic Patterns
  const { data: patternsData, isLoading: patternsLoading } = useQuery({
    queryKey: ['edit-intelligence-patterns', selectedValidationId],
    queryFn: () => editIntelligenceApi.getPatterns(queryParams),
  });
  const patterns = patternsData?.data?.patterns || [];

  // Root Causes — backend requires a specific validationId (per-file analysis),
  // so only fetch once one is selected. Without this guard the "All Validations"
  // default fires a request with no validationId and the API returns a 400
  // ("validationId query parameter required"), surfacing an error toast on load.
  const { data: rootCausesData, isLoading: rootCausesLoading } = useQuery({
    queryKey: ['edit-intelligence-root-causes', selectedValidationId],
    queryFn: () => editIntelligenceApi.getRootCauses(queryParams),
    enabled: !!selectedValidationId,
  });
  const rootCauses = rootCausesData?.data?.rootCauses || [];

  // Priority Ranking — same per-validation requirement as root causes above.
  const { data: priorityData, isLoading: priorityLoading } = useQuery({
    queryKey: ['edit-intelligence-priority', selectedValidationId],
    queryFn: () => editIntelligenceApi.getPriorityRanking(queryParams),
    enabled: !!selectedValidationId,
  });
  const priorities = priorityData?.data?.rankings || [];

  // Field Mapping Check
  const { data: fieldMappingData, isLoading: fieldMappingLoading } = useQuery({
    queryKey: ['edit-intelligence-field-mapping', selectedValidationId],
    queryFn: () => editIntelligenceApi.getFieldMappingCheck(queryParams),
  });
  const fieldMappings = fieldMappingData?.data?.issues || [];

  const isLoading = patternsLoading || rootCausesLoading || priorityLoading || fieldMappingLoading;

  return (
    <FeatureGate feature="edit_intelligence" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Intelligence</h1>
            <p className="mt-1 text-sm text-gray-500">
              Analyze HMDA edit patterns, root causes, and priority fixes
            </p>
          </div>
          <div>
            <select
              value={selectedValidationId}
              onChange={(e) => setSelectedValidationId(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
            >
              <option value="">All Validations</option>
              {validations.map((v: any) => (
                <option key={v.id} value={v.id}>
                  {v.fileName || v.id} - {new Date(v.createdAt).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <LightBulbIcon className="h-5 w-5 text-purple-500" />
              <p className="text-xs font-medium text-gray-500">Systemic Patterns</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">{patterns.length}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <MagnifyingGlassIcon className="h-5 w-5 text-orange-500" />
              <p className="text-xs font-medium text-gray-500">Root Causes Found</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">{rootCauses.length}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <ArrowTrendingUpIcon className="h-5 w-5 text-blue-500" />
              <p className="text-xs font-medium text-gray-500">Priority Edits</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">{priorities.length}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
              <p className="text-xs font-medium text-gray-500">Field Mapping Issues</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">{fieldMappings.length}</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            {/* Systemic Patterns */}
            <div className="rounded-lg border bg-white">
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Systemic Patterns</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Recurring edit failures that suggest systemic data entry or mapping issues
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Edit Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Field Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Occurrences</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Most Common Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Value Distribution</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {patterns.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-400">
                          No systemic patterns detected
                        </td>
                      </tr>
                    ) : (
                      patterns.map((p: any, i: number) => {
                        const occurrences = p.totalOccurrences ?? p.occurrences ?? 0;
                        const dominantValue = p.dominantValue?.value ?? p.mostCommonValue ?? '—';
                        const frequencyPct = p.dominantValue?.pct ?? p.frequencyPct;
                        const uniqueValues = p.uniqueValues;
                        // When there's only 1 distinct value across all failures
                        // for this pattern, "Frequency %" is always 100% and
                        // therefore uninformative — surface that to the user.
                        const distributionLabel =
                          frequencyPct == null
                            ? '—'
                            : uniqueValues === 1
                              ? <>100% <span className="text-xs text-gray-400">(only value)</span></>
                              : <>{frequencyPct.toFixed ? frequencyPct.toFixed(1) : frequencyPct}% <span className="text-xs text-gray-400">of {uniqueValues} values</span></>;
                        return (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{p.editCode}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{p.fieldName}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 tabular-nums">{occurrences}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                              <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{dominantValue}</code>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 tabular-nums">
                              {distributionLabel}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Root Causes */}
            <div className="rounded-lg border bg-white">
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Root Causes</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Suggested root causes for edit failures based on pattern analysis
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Edit Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Suggested Root Cause</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Affected Loans</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Severity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rootCauses.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400">
                          {selectedValidationId
                            ? 'No root causes identified'
                            : 'Select a specific validation above to see root-cause analysis'}
                        </td>
                      </tr>
                    ) : (
                      rootCauses.map((rc: any, i: number) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{rc.editCode}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{rc.suggestedRootCause}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{rc.affectedLoans}</td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span
                              className={clsx(
                                'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                                SEVERITY_BADGE[rc.severity] || SEVERITY_BADGE.LOW
                              )}
                            >
                              {rc.severity}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Priority Ranking */}
            <div className="rounded-lg border bg-white">
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Priority Ranking</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Edits ranked by priority score to guide remediation efforts
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Edit Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Field</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Priority Score</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Error Count</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Suggested Fix</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {priorities.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-400">
                          {selectedValidationId
                            ? 'No priority rankings available'
                            : 'Select a specific validation above to see priority ranking'}
                        </td>
                      </tr>
                    ) : (
                      priorities.map((pr: any, i: number) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-bold text-gray-900">{pr.rank || i + 1}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{pr.editCode}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{pr.field}</td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-16 rounded-full bg-gray-200">
                                <div
                                  className={clsx(
                                    'h-2 rounded-full',
                                    (pr.priorityScore || 0) >= 80 ? 'bg-red-500' :
                                    (pr.priorityScore || 0) >= 50 ? 'bg-yellow-500' : 'bg-green-500'
                                  )}
                                  style={{ width: `${Math.min(pr.priorityScore || 0, 100)}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-900">{pr.priorityScore}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{pr.errorCount}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{pr.suggestedFix}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Field Mapping Check */}
            <div className="rounded-lg border bg-white">
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Field Mapping Check</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Fields where more than 50% of values match one particular value, indicating potential mapping issues
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Field Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Dominant Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Percentage</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total Records</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Risk</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {fieldMappings.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-400">
                          No field mapping issues detected
                        </td>
                      </tr>
                    ) : (
                      fieldMappings.map((fm: any, i: number) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{fm.fieldName}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                            <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{fm.dominantValue}</code>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span
                              className={clsx(
                                'text-sm font-medium',
                                (fm.percentage || 0) >= 90 ? 'text-red-600' :
                                (fm.percentage || 0) >= 70 ? 'text-yellow-600' : 'text-gray-900'
                              )}
                            >
                              {fm.percentage != null ? `${fm.percentage.toFixed(1)}%` : '--'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{fm.totalRecords}</td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span
                              className={clsx(
                                'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                                (fm.percentage || 0) >= 90 ? 'bg-red-100 text-red-700' :
                                (fm.percentage || 0) >= 70 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-blue-100 text-blue-700'
                              )}
                            >
                              {(fm.percentage || 0) >= 90 ? 'Critical' :
                               (fm.percentage || 0) >= 70 ? 'Warning' : 'Review'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </FeatureGate>
  );
}
